"use client";

import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";

import Footer from "../components/Footer";
import Header from "../components/Header";
import JobApplicationForm from "../components/JobApplicationForm";
import styles from "../styles/Jobs.module.css";

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("All");
  const [sessionId, setSessionId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [totalJobs, setTotalJobs] = useState(0);
  const [expandedJobs, setExpandedJobs] = useState(new Set());
  const jobRefs = useRef({});

  const location = useLocation();
  const navigate = useNavigate();
  const { jobId, slug } = useParams();

  const jobsPerPage = 5;

  const createSlug = (title) =>
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const getNewSessionId = async () => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/web/session/authenticate`,
        {
          jsonrpc: "2.0",
          params: {
            db: "ProdClone",
            login: import.meta.env.VITE_API_AUTH_USERNAME,
            password: import.meta.env.VITE_API_AUTH_PASSWORD,
          },
        },
        { headers: { "Content-Type": "application/json" }, withCredentials: true }
      );
      const match = document.cookie.match(/(?:^|;\s*)session_id=([^;]*)/);
      return match ? match[1] : "cookie-managed";
    } catch (error) {
      console.error("Failed to get session ID:", error);
      return null;
    }
  };

  const fetchJobs = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/jobs`,
        { page, per_page: jobsPerPage },
        { headers: { "Content-Type": "application/json" }, withCredentials: true }
      );
      const jobList = response.data?.result?.result?.jobs ?? [];
      const total = response.data?.result?.result?.total ?? 0;
      setJobs(jobList);
      setTotalJobs(total);
      setTotalPages(Math.ceil(total / jobsPerPage));
    } catch (err) {
      console.error("Failed to fetch jobs", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeSession = async () => {
      const newSessionId = await getNewSessionId();
      if (newSessionId) setSessionId(newSessionId);
    };
    initializeSession();
  }, []);

  useEffect(() => {
    if (sessionId) {
      fetchJobs(currentPage);
    }
  }, [currentPage, sessionId]);

  // Scroll to job on initial load based on URL
  useEffect(() => {
    if (jobId && jobs.length > 0) {
      const targetJob = jobs.find((j) => (j._id || j.id) === jobId);
      if (targetJob && jobRefs.current[jobId]) {
        jobRefs.current[jobId].scrollIntoView({ behavior: "smooth" });
        const expectedSlug = createSlug(targetJob.title);
        if (slug !== expectedSlug) {
          navigate(`/job/${jobId}}`, { replace: true });
        }
      } else {
        // If job not in current page, find its page and load it
        const jobIndex = totalJobs > 0 ? totalJobs.findIndex?.((j) => (j._id || j.id) === jobId) : -1;
        if (jobIndex >= 0) {
          const pageContainingJob = Math.floor(jobIndex / jobsPerPage) + 1;
          setCurrentPage(pageContainingJob);
        } else {
          navigate("/jobs");
        }
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [jobId, slug, jobs, navigate]);

  // Intersection Observer to update URL while scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(async (entry) => {
          if (entry.isIntersecting) {
            const visibleJobId = entry.target.getAttribute("data-job-id");
            const job = jobs.find((j) => (j._id || j.id) === visibleJobId);

            if (job) {
              const slug = createSlug(job.title);
              // const currentPath = `/jobs/${visibleJobId}/${slug}`;

              if (location.pathname !== currentPath) {
                navigate(currentPath, { replace: true });
              }
            } else {
              // If job is not loaded, calculate which page it should be on
              const jobIndex = totalJobs > 0 ? totalJobs.findIndex?.((j) => (j._id || j.id) === visibleJobId) : -1;
              if (jobIndex >= 0) {
                const pageContainingJob = Math.floor(jobIndex / jobsPerPage) + 1;
                if (pageContainingJob !== currentPage) setCurrentPage(pageContainingJob);
              }
            }
          }
        });
      },
      { root: null, threshold: 0.5, rootMargin: "0px" }
    );

    Object.values(jobRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      Object.values(jobRefs.current).forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [jobs, currentPage, navigate, location.pathname, totalJobs]);

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setShowApplicationForm(true);
  };

  const handleCloseApplicationForm = () => {
    setShowApplicationForm(false);
    setSelectedJob(null);
  };

  const toggleJobExpansion = (jobKey) => {
    setExpandedJobs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(jobKey)) newSet.delete(jobKey);
      else newSet.add(jobKey);
      return newSet;
    });
  };

  const truncateHtmlContent = (htmlContent, maxLength = 200) => {
    if (!htmlContent) return "";
    const textContent = htmlContent.replace(/<[^>]*>/g, "");
    if (textContent.length <= maxLength) return htmlContent;

    let truncateAt = maxLength;
    const sentences = textContent.split(/[.!?]+/);
    let currentLength = 0;

    for (let i = 0; i < sentences.length; i++) {
      const sentenceLength = sentences[i].length + 1;
      if (currentLength + sentenceLength > maxLength) break;
      currentLength += sentenceLength;
      truncateAt = currentLength;
    }

    if (truncateAt === maxLength) {
      const words = textContent.substring(0, maxLength).split(" ");
      words.pop();
      const truncatedText = words.join(" ");
      return htmlContent.includes("<p>") ? `<p>${truncatedText}...</p>` : `${truncatedText}...`;
    }

    const truncatedText = textContent.substring(0, truncateAt);
    return htmlContent.includes("<p>") ? `<p>${truncatedText}</p>` : truncatedText;
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  return (
    <div>
      <Header />
      <div className={styles.container}>
        <div className={styles.maxWidth}>
          <p className={styles.blogTitle}>ARIF JOBS</p>
          <h2 className={styles.heading}>
            Arif Jobs: be a part of
            <span className={styles.mobileBreak}><br /></span> our community
          </h2>
        </div>
      </div>

      {loading ? (
        <p style={{ textAlign: "center", margin: "2rem" }}>Loading jobs...</p>
      ) : jobs.length === 0 ? (
        <p style={{ textAlign: "center", margin: "2rem" }}>
          {sessionId ? "No jobs found." : "Failed to load jobs. Please refresh."}
        </p>
      ) : (
        jobs.map((job, index) => {
          const jobKey = job._id || job.id || index;
          const formattedDate = job.createdAt ? new Date(job.createdAt).toLocaleDateString() : null;
          const isExpanded = expandedJobs.has(jobKey);
          const jobDescription = job.description || "<p>No description available</p>";
          const shouldShowReadMore = jobDescription.replace(/<[^>]*>/g, "").length > 200;

          return job.is_published ? (
            <div
              key={jobKey}
              className={styles.internationalPaymentsSection}
              data-job-id={jobKey}
              ref={(el) => (jobRefs.current[jobKey] = el)}
            >
              <div className={styles.paymentDetails}>
                <h2 style={{ marginTop: "0" }}>
                  <Link
                    to={`/job/${job.id}/`}
                    style={{ color: "inherit", textDecoration: "none" }}
                    onClick={() => {
                      if (jobRefs.current[jobKey]) {
                        jobRefs.current[jobKey].scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                  >
                    {job.title}
                  </Link>
                </h2>

                <div style={{ marginTop: "8px", color: "black" }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: isExpanded
                        ? jobDescription
                        : truncateHtmlContent(jobDescription, 200),
                    }}
                  />
                  {shouldShowReadMore && (
                    <button
                      onClick={() => toggleJobExpansion(jobKey)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--primary-green, #007bff)",
                        cursor: "pointer",
                        textDecoration: "underline",
                        fontSize: "14px",
                        marginTop: "8px",
                        padding: "0",
                      }}
                    >
                      {isExpanded ? "Read Less" : "Read More"}
                    </button>
                  )}
                </div>

                {job.location && (
                  <div className={styles.locationContainer}>
                    <p className={styles.locationText}>{job.location}</p>
                  </div>
                )}

                {formattedDate && (
                  <div className={styles.locationContainer}>
                    <p className={styles.locationText}>{formattedDate}</p>
                  </div>
                )}

                <button
                  onClick={() => handleApplyClick(job)}
                  style={{
                    backgroundColor: "green",
                    color: "white",
                    padding: "10px 20px",
                    border: "none",
                    borderRadius: "10px",
                    marginTop: "20px",
                    cursor: "pointer",
                  }}
                >
                  Apply Now →
                </button>
              </div>
            </div>
          ) : null;
        })
      )}

      <div className={styles.pagination}>
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          className={styles.paginationButton}
        >
          Previous
        </button>
        <span>Page {currentPage} of {totalPages}</span>
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className={styles.paginationButton}
        >
          Next
        </button>
      </div>

      <JobApplicationForm
        jobTitle={selectedJob?.title || ""}
        jobId={selectedJob?.id || ""}
        isOpen={showApplicationForm}
        onClose={handleCloseApplicationForm}
      />

      <Footer />
    </div>
  );
};

export default Jobs;









// "use client";

// import axios from "axios";

// import { useEffect, useState, useRef } from "react";

// import { useParams, useLocation, useNavigate } from "react-router-dom";

// import Footer from "../components/Footer";

// import Header from "../components/Header";

// import JobApplicationForm from "../components/JobApplicationForm";

// import styles from "../styles/Jobs.module.css";

// const Jobs = () => {

//   const [jobs, setJobs] = useState([]);

//   const [loading, setLoading] = useState(true);

//   const [selectedJob, setSelectedJob] = useState(null);

//   const [searchTerm, setSearchTerm] = useState("");

//   const [filter, setFilter] = useState("All");

//   const [sessionId, setSessionId] = useState("");

//   const [currentPage, setCurrentPage] = useState(1);

//   const [totalPages, setTotalPages] = useState(1);

//   const [showApplicationForm, setShowApplicationForm] = useState(false);

//   const [totalJobs, setTotalJobs] = useState(0);

//   const [expandedJobs, setExpandedJobs] = useState(new Set());

//   const jobRefs = useRef({}); // Store references to job sections

//   const location = useLocation();

//   const navigate = useNavigate();

//   const { jobId, slug } = useParams(); // Get jobId and slug from URL

//   const jobsPerPage = 5;

//   // Helper function to create URL-friendly slug

//   const createSlug = (title) => {

//     return title

//       .toLowerCase()

//       .replace(/[^a-z0-9]+/g, "-")

//       .replace(/(^-|-$)/g, "");

//   };

// // Inside the Jobs component

// // Scroll to job section when navigating to /jobs/:jobId/:slug

// useEffect(() => {

//   if (jobId && jobs.length > 0) {

//     const targetJob = jobs.find((j) => (j._id || j.id) === jobId);

//     if (targetJob && jobRefs.current[jobId]) {

//       jobRefs.current[jobId].scrollIntoView({ behavior: "smooth" });

//       const expectedSlug = createSlug(targetJob.title);

//       if (slug !== expectedSlug) {

//         navigate(`/jobs/${jobId}/${expectedSlug}`, { replace: true });

//       }

//     } else {

//       // Redirect to /jobs if jobId is invalid

//       navigate("/jobs");

//     }

//   } else {

//     window.scrollTo(0, 0); // Scroll to top for /jobs

//   }

// }, [jobId, slug, jobs, navigate]);

// // Set up Intersection Observer to update URL when job section is visible

// useEffect(() => {

//   const observer = new IntersectionObserver(

//     (entries) => {

//       entries.forEach((entry) => {

//         if (entry.isIntersecting) {

//           const jobId = entry.target.getAttribute("data-job-id");

//           const job = jobs.find((j) => (j._id || j.id) === jobId);

//           if (job) {

//             const slug = createSlug(job.title);

//             const currentPath = `/jobs/${jobId}/${slug}`;

//             // Only update URL if it differs

//             if (location.pathname !== currentPath) {

//               navigate(currentPath, { replace: true });

//               console.log("Navigated to:", currentPath); // Debug log

//             }

//           }

//         }

//       });

//     },

//     {

//       root: null, // Use viewport as root

//       threshold: 0.5, // Reduced to 50% to make it easier to trigger

//       rootMargin: "0px", // No margin adjustment

//     }

//   );

//   // Observe each job section

//   Object.values(jobRefs.current).forEach((ref) => {

//     if (ref) {

//       observer.observe(ref);

//       console.log("Observing job with ID:", ref.getAttribute("data-job-id")); // Debug log

//     }

//   });

//   // Cleanup observer on unmount

//   return () => {

//     Object.values(jobRefs.current).forEach((ref) => {

//       if (ref) observer.unobserve(ref);

//     });

//   };

// }, [jobs, navigate, location.pathname]);

//   const handleApplyClick = (job) => {

//     setSelectedJob(job);

//     setShowApplicationForm(true);

//   };

//   const handleCloseApplicationForm = () => {

//     setShowApplicationForm(false);

//     setSelectedJob(null);

//   };

//   const toggleJobExpansion = (jobKey) => {

//     setExpandedJobs((prev) => {

//       const newSet = new Set(prev);

//       if (newSet.has(jobKey)) {

//         newSet.delete(jobKey);

//       } else {

//         newSet.add(jobKey);

//       }

//       return newSet;

//     });

//   };

//   const truncateHtmlContent = (htmlContent, maxLength = 200) => {

//     if (!htmlContent) return "";

//     const textContent = htmlContent.replace(/<[^>]*>/g, "");

//     if (textContent.length <= maxLength) return htmlContent;

//     let truncateAt = maxLength;

//     const sentences = textContent.split(/[.!?]+/);

//     let currentLength = 0;

//     for (let i = 0; i < sentences.length; i++) {

//       const sentenceLength = sentences[i].length + 1;

//       if (currentLength + sentenceLength > maxLength) break;

//       currentLength += sentenceLength;

//       truncateAt = currentLength;

//     }

//     if (truncateAt === maxLength) {

//       const words = textContent.substring(0, maxLength).split(" ");

//       words.pop();

//       const truncatedText = words.join(" ");

//       return htmlContent.includes("<p>")

//         ? `<p>${truncatedText}...</p>`

//         : `${truncatedText}...`;

//     }

//     const truncatedText = textContent.substring(0, truncateAt);

//     return htmlContent.includes("<p>")

//       ? `<p>${truncatedText}...</p>`

//       : `${truncatedText}...`;

//   };

//   const getNewSessionId = async () => {

//     try {

//       const response = await axios.post(

//         `${import.meta.env.VITE_API_BASE_URL}/web/session/authenticate`,

//         {

//           jsonrpc: "2.0",

//           params: {

//             db: "ProdClone",

//             login: import.meta.env.VITE_API_AUTH_USERNAME,

//             password: import.meta.env.VITE_API_AUTH_PASSWORD,

//           },

//         },

//         {

//           headers: {

//             "Content-Type": "application/json",

//           },

//           withCredentials: true,

//         }

//       );

//       const match = document.cookie.match(/(?:^|;\s*)session_id=([^;]*)/);

//       return match ? match[1] : "cookie-managed";

//     } catch (error) {

//       console.error("Failed to get session ID:", error);

//       return null;

//     }

//   };

//   const fetchJobs = async (page = 1) => {

//     setLoading(true);

//     try {

//       const response = await axios.post(

//         `${import.meta.env.VITE_API_BASE_URL}/api/jobs`,

//         {

//           page,

//           per_page: jobsPerPage,

//         },

//         {

//           headers: {

//             "Content-Type": "application/json",

//           },

//           withCredentials: true,

//         }

//       );

//       const jobList = response.data?.result?.result?.jobs ?? [];

//       const total = response.data?.result?.result?.total ?? 0;

//       setJobs(jobList);

//       setTotalJobs(total);

//       setTotalPages(Math.ceil(total / jobsPerPage));

//     } catch (err) {

//       console.error("Failed to fetch jobs", err);

//     } finally {

//       setLoading(false);

//     }

//   };

//   useEffect(() => {

//     const initializeSession = async () => {

//       const newSessionId = await getNewSessionId();

//       if (newSessionId) setSessionId(newSessionId);

//     };

//     initializeSession();

//   }, []);

//   useEffect(() => {

//     if (sessionId) {

//       fetchJobs(currentPage);

//     }

//   }, [currentPage, sessionId]);

//   const handlePrevPage = () => {

//     if (currentPage > 1) setCurrentPage((prev) => prev - 1);

//   };

//   const handleNextPage = () => {

//     if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);

//   };

//   return (

//     <div>

//       <Header />

//       <div className={styles.container}>

//         <div className={styles.maxWidth}>

//           <p className={styles.blogTitle}>ARIF JOBS</p>

//           <h2 className={styles.heading}>

//             Arif Jobs: be a part of

//             <span className={styles.mobileBreak}><br /></span> our community

//           </h2>

//         </div>

//       </div>

//       {loading ? (

//         <p style={{ textAlign: "center", margin: "2rem" }}>Loading jobs...</p>

//       ) : jobs.length === 0 ? (

//         <p style={{ textAlign: "center", margin: "2rem" }}>

//           {sessionId ? "No jobs found." : "Failed to load jobs. Please refresh."}

//         </p>

//       ) : (

//         jobs.map((job, index) => {

//           const jobKey = job._id || job.id || index;

//           const formattedDate = job.createdAt ? new Date(job.createdAt).toLocaleDateString() : null;

//           const isExpanded = expandedJobs.has(jobKey);

//           const jobDescription = job.description || "<p>No description available</p>";

//           const shouldShowReadMore = jobDescription.replace(/<[^>]*>/g, "").length > 200;

//           return job.is_published ? (

//             <div

//               key={jobKey}

//               className={styles.internationalPaymentsSection}

//               data-job-id={jobKey}

//               ref={(el) => (jobRefs.current[jobKey] = el)} // Assign ref to job section

//             >

//               <div className={styles.paymentDetails}>

//                 <h2 style={{ marginTop: "0" }}>{job.title}</h2>

//                 <div style={{ marginTop: "8px", color: "black" }}>

//                   {/* <h3>

//                     <strong style={{ fontSize: "16px" }}>Job Summary:</strong>

//                   </h3> */}

//                   <div

//                     dangerouslySetInnerHTML={{

//                       __html: isExpanded

//                         ? jobDescription

//                         : truncateHtmlContent(jobDescription, 200),

//                     }}

//                   />

//                   {shouldShowReadMore && (

//                     <button

//                       onClick={() => toggleJobExpansion(jobKey)}

//                       style={{

//                         background: "none",

//                         border: "none",

//                         color: "var(--primary-green, #007bff)",

//                         cursor: "pointer",

//                         textDecoration: "underline",

//                         fontSize: "14px",

//                         marginTop: "8px",

//                         padding: "0",

//                       }}

//                     >

//                       {isExpanded ? "Read Less" : "Read More"}

//                     </button>

//                   )}

//                 </div>

//                 {job.location && (

//                   <div className={styles.locationContainer}>

//                     <p className={styles.locationText}>{job.location}</p>

//                   </div>

//                 )}

//                 {formattedDate && (

//                   <div className={styles.locationContainer}>

//                     <p className={styles.locationText}>{formattedDate}</p>

//                   </div>

//                 )}

//                 <button

//                   onClick={() => handleApplyClick(job)}

//                   style={{

//                     backgroundColor: "green",

//                     color: "white",

//                     padding: "10px 20px",

//                     border: "none",

//                     borderRadius: "10px",

//                     marginTop: "20px",

//                     cursor: "pointer",

//                   }}

//                 >

//                   Apply Now →

//                 </button>

//               </div>

//             </div>

//           ) : null;

//         })

//       )}

//       <div className={styles.pagination}>

//         <button

//           onClick={handlePrevPage}

//           disabled={currentPage === 1}

//           className={styles.paginationButton}

//         >

//           Previous

//         </button>

//         <span>Page {currentPage} of {totalPages}</span>

//         <button

//           onClick={handleNextPage}

//           disabled={currentPage === totalPages}

//           className={styles.paginationButton}

//         >

//           Next

//         </button>

//       </div>

//       <JobApplicationForm

//         jobTitle={selectedJob?.title || ""}

//         jobId={selectedJob?.id || ""}

//         isOpen={showApplicationForm}

//         onClose={handleCloseApplicationForm}

//       />

//       <Footer />

//     </div>

//   );

// };

// export default Jobs;

